/* eslint-disable indent */
import { OnScreenComponent } from 'components/abstract/OnScreenComponent'
import { Button } from 'components/common/Button'
import Image, { ImageProps } from 'next/image'
import { useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useResizeDetector } from 'react-resize-detector'
import {
  TSize,
  TSrc,
  isResponsiveSize,
  isResponsiveSrc,
} from '../../utils/image'

type Props = {
  className?: string
  children?: React.ReactNode
  src: TSrc
  alt?: string
  vertical?: boolean
  type?: 'royalties' | 'blurred' | 'half-blurred'
  title?: string
  subtitle?: React.ReactNode
  artistName?: string
  reversed?: boolean
  isFocused?: boolean
  width?: TSize
  height?: TSize
  imgTypes?: string[]
  renderImageWhenOnScreen?: boolean
  bottomBlurredOnMobile?: boolean
  breakpoint?: number
  fillOpacity?: string
  priority?: boolean
  sizes?: TSize
  quality?: TSize
  placeholder?: ImageProps['placeholder']
}

export const ImgSkeleton = () => (
  <Skeleton width='100%' height='100%' className='absolute left-0 top-0' />
)

export const Banner = ({
  className = '',
  children,
  src,
  alt = '',
  vertical = false,
  type,
  artistName,
  reversed,
  isFocused,
  width = { mobile: 0, desktop: 0 },
  height: initialHeight = { mobile: 0, desktop: 0 },
  renderImageWhenOnScreen = false,
  bottomBlurredOnMobile,
  fillOpacity = '0.01',
  priority = false,
  sizes,
  quality = 100,
  placeholder,
}: Props) => {
  const [imgIsLoaded, setImgIsLoaded] = useState(false)
  const { height: refHeight, ref } = useResizeDetector()

  const height = initialHeight || refHeight || 0

  const onLoadingComplete = () => {
    setImgIsLoaded(true)
  }

  const imgProps: Partial<ImageProps> = {
    onLoad: onLoadingComplete,
    priority,
    placeholder,
  }

  const imgEl = (
    <>
      {isResponsiveSrc(src) &&
        isResponsiveSize(width) &&
        isResponsiveSize(height) && (
          <>
            {src.desktop && (
              <Image
                src={src.desktop}
                alt={alt}
                width={Number(width.desktop)}
                height={Number(height.desktop)}
                className='hidden h-full w-full object-cover object-center sm:block'
                quality={
                  Number(
                    isResponsiveSize(quality) ? quality.desktop : quality
                  ) || 100
                }
                sizes={
                  sizes && isResponsiveSize(sizes)
                    ? String(sizes.desktop)
                    : '(max-width:640px) 0vw, 100vw'
                }
                {...imgProps}
              />
            )}
            {src.mobile && (
              <Image
                src={src.mobile}
                alt={alt}
                width={Number(width.mobile)}
                height={Number(height.mobile)}
                className='h-full w-full object-cover object-center sm:hidden'
                quality={
                  Number(
                    isResponsiveSize(quality) ? quality.mobile : quality
                  ) || 100
                }
                sizes={
                  sizes && isResponsiveSize(sizes)
                    ? String(sizes.mobile)
                    : '(max-width:639px) 100vw, 0vw'
                }
                {...imgProps}
              />
            )}
          </>
        )}
    </>
  )

  const img = renderImageWhenOnScreen ? (
    <OnScreenComponent className='h-full' rootMargin={-5} threshold={0.25}>
      {imgEl}
      {!imgIsLoaded && <ImgSkeleton />}
    </OnScreenComponent>
  ) : (
    imgEl
  )

  if (type === 'half-blurred') {
    return (
      <div
        ref={ref}
        tabIndex={isFocused ? 0 : -1}
        className={`relative h-[350px] w-full sm:h-[300px] ${className}`}
      >
        <div className='absolute h-full w-full'>
          {img}
          {/* {!imgIsLoaded && <ImgSkeleton />} */}
        </div>

        {bottomBlurredOnMobile && (
          <div className='flex h-full w-full items-end sm:hidden'>
            <div
              className={`w-full bg-black ${
                fillOpacity
                  ? `bg-opacity-[${fillOpacity}]`
                  : 'bg-opacity-[0.01]'
              } px-6 pb-[30px] pt-4 backdrop-blur-[30px]`}
            >
              {children}
            </div>
          </div>
        )}

        <div
          className={`${
            bottomBlurredOnMobile ? 'hidden' : 'block'
          } h-full w-full sm:block`}
        >
          <div
            className={`absolute h-full w-1/2 bg-[rgb(250,250,250)] bg-opacity-85 backdrop-blur-2xl ${
              reversed && 'right-0'
            }`}
          />

          <div
            className={`container relative  h-full w-full ${
              reversed && 'flex justify-end'
            }`}
          >
            <div
              className={`flex h-full w-1/2 flex-col justify-center ${
                reversed ? 'pl-6 md:pl-12' : 'pr-6 md:pr-12'
              }`}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (type === 'blurred') {
    return (
      <div
        ref={ref}
        className={`relative bg-cover bg-center bg-no-repeat py-20 ${className}`}
      >
        <div className='absolute left-0 top-0 h-full w-full'>
          {img}
          {/* {!imgIsLoaded && <ImgSkeleton />} */}
        </div>

        <div className='container'>
          <div className='container flex w-fit flex-col items-center rounded-xl bg-cool-50 bg-opacity-80 px-12 py-8 backdrop-blur-2xl'>
            {!children && (
              <>
                <p className='mb-2 text-center text-lg text-cool-700'>
                  What are you waiting for?
                </p>
                <h5 className='text-center font-medium text-cool-900 lg:hidden'>
                  Buy & Sell from 1000s of artworks
                </h5>
                <h4 className='hidden whitespace-nowrap text-center font-medium text-cool-900 lg:block'>
                  Buy & Sell from 1000s of artworks
                </h4>
                <Button size='lg' type='link' href='/artworks' className='mt-8'>
                  View all artworks
                </Button>
              </>
            )}
            {children}
          </div>
        </div>
      </div>
    )
  }

  if (type === 'royalties' && artistName) {
    return (
      <div
        className={`relative flex w-full flex-col items-center overflow-hidden rounded-lg bg-slate p-8 text-center shadow-[0_2px_10px_0_rgba(38,35,56,0.2)] lg:items-start lg:px-20 lg:py-14 lg:text-start ${className}`}
      >
        <div className='pointer-events-none absolute inset-0 opacity-3'>
          <Image
            src='/files/bg-royalty-banner.png'
            alt='royalties'
            fill
            className='object-cover'
          />
        </div>

        <div className='relative min-h-[100px] min-w-[100px] lg:absolute lg:right-0 lg:top-1/2 lg:h-[350px] lg:w-[350px] lg:-translate-y-1/2 lg:translate-x-5/20 lg:rounded-l-full'>
          {typeof src === 'string' && (
            <Image
              src={src}
              alt={artistName}
              fill
              className='rounded-full object-contain object-center'
            />
          )}
        </div>

        {children}
      </div>
    )
  }

  return (
    <div
      className={`${className} relative overflow-hidden rounded-2xl border border-cool-400`}
      style={{
        minHeight: refHeight ? refHeight + 2 * 48 : undefined,
      }}
    >
      {vertical && (
        <div
          ref={ref}
          className='absolute left-0 z-10 flex w-full flex-col items-start justify-center bg-cool-800 bg-opacity-97 p-12 text-cool-50'
        >
          {children}
        </div>
      )}

      {!vertical && (
        <div
          ref={ref}
          className='absolute bottom-0 z-10 flex w-full flex-col items-start justify-center bg-cool-200 bg-opacity-95 p-12 text-cool-800 md:left-0 md:min-h-full md:w-auto md:max-w-md xl:p-16'
        >
          {children}
        </div>
      )}
      {img}
      {/* {!imgIsLoaded && <ImgSkeleton />} */}
    </div>
  )
}
